import { useEffect, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabase'

/**
 * A mutable holder for the current session.
 *
 * Why this exists: TanStack Router's `beforeLoad` guard runs SYNCHRONOUSLY, but
 * restoring a session from localStorage is asynchronous. Reading the session
 * inside the guard would therefore see `null` on first paint and bounce a
 * logged-in user to /login. Instead we resolve the session once before the app
 * renders (see main.tsx) and hand the router this object to read synchronously.
 */
export const auth: { session: Session | null } = { session: null }

/**
 * Resolve the stored session, then keep it up to date.
 *
 * `onChange` is called whenever the session changes -- on login, logout, and on
 * every silent token refresh -- so the router and query cache can react.
 */
export async function initAuth(onChange: () => void): Promise<void> {
  const { data } = await supabase.auth.getSession()
  auth.session = data.session

  supabase.auth.onAuthStateChange((_event, session) => {
    auth.session = session
    onChange()
  })
}

/**
 * Reactive access to the current session, for components that need to render
 * differently when signed in.
 *
 * The `auth` object above is deliberately a plain mutable holder so the router
 * can read it synchronously -- but that means React has no idea when it
 * changes. This hook subscribes properly, so a component using it re-renders on
 * login, logout, and token refresh.
 */
export function useSession(): Session | null {
  const [session, setSession] = useState<Session | null>(auth.session)

  useEffect(() => {
    // initAuth may have resolved between our initial state and this effect.
    setSession(auth.session)
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next))
    return () => data.subscription.unsubscribe()
  }, [])

  return session
}
